import { useState } from 'react';
import { motion } from 'framer-motion';

export default function ProcessTimeline() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "Combien de temps dure un projet de rénovation LED ?",
      answer: "La durée totale varie selon la taille du projet, mais en moyenne : 1-2 jours pour l'audit initial, 5-7 jours pour l'étude et le devis, 2-4 semaines pour la fabrication des luminaires, et 1-5 jours pour l'installation selon la surface. Un projet complet prend généralement 4 à 8 semaines du premier contact à la mise en service.",
      icon: "ri-time-line",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      question: "Quelles économies puis-je réaliser avec le LED ?",
      answer: "Le passage au LED permet de réaliser jusqu'à 75% d'économies sur votre facture d'éclairage. Pour un entrepôt de 2000m², cela représente environ 15 000€ d'économies annuelles. De plus, la durée de vie des LED (50 000h) réduit considérablement les coûts de maintenance.",
      icon: "ri-money-euro-circle-line",
      gradient: "from-emerald-500 to-teal-500"
    },
    {
      question: "Comment fonctionnent les Certificats d'Économies d'Énergie (CEE) ?",
      answer: "Les CEE sont des aides financières de l'État qui peuvent couvrir jusqu'à 100% du coût de votre installation LED. Nous nous occupons de toutes les démarches administratives : constitution du dossier, dépôt auprès de l'organisme certificateur, et obtention des primes. Vous n'avez rien à faire !",
      icon: "ri-file-shield-2-line",
      gradient: "from-purple-500 to-indigo-500"
    },
    {
      question: "L'étude photométrique est-elle vraiment gratuite ?",
      answer: "Oui, absolument ! Notre étude photométrique professionnelle est 100% gratuite et sans engagement. Elle comprend une analyse complète de votre éclairage actuel, des simulations 3D de l'éclairage futur, et des recommandations personnalisées. C'est notre façon de vous montrer notre expertise avant tout engagement.",
      icon: "ri-lightbulb-flash-line",
      gradient: "from-amber-500 to-orange-500"
    },
    {
      question: "Quelles garanties offrez-vous sur vos installations ?",
      answer: "Nous offrons une garantie complète de 5 ans sur tous nos luminaires et installations. Cela couvre les pièces, la main d'œuvre et les déplacements. De plus, nos luminaires LED ont une durée de vie de 50 000 heures, soit environ 15 ans d'utilisation normale.",
      icon: "ri-shield-check-line",
      gradient: "from-rose-500 to-pink-500"
    },
    {
      question: "Puis-je continuer mon activité pendant les travaux ?",
      answer: "Oui ! Nous planifions les interventions pour minimiser l'impact sur votre activité. Les installations peuvent être réalisées en dehors des heures d'ouverture, le week-end, ou par zones pour maintenir votre activité. Nous nous adaptons à vos contraintes opérationnelles.",
      icon: "ri-building-line",
      gradient: "from-cyan-500 to-blue-500"
    },
    {
      question: "Quels types de bâtiments peuvent bénéficier de vos services ?",
      answer: "Nous intervenons sur tous types de bâtiments professionnels : entrepôts, usines, commerces, bureaux, installations sportives, établissements de santé, collectivités, parkings... Chaque projet bénéficie d'une solution sur mesure adaptée à ses spécificités.",
      icon: "ri-community-line",
      gradient: "from-violet-500 to-purple-500"
    },
    {
      question: "Quelles sont les options de paiement et de financement ?",
      answer: "Grâce aux CEE, votre installation peut vous coûter seulement 1€ symbolique. Si un reste à charge existe, nous proposons des solutions de paiement échelonné et des partenariats avec des organismes de financement. L'objectif : rendre votre projet accessible immédiatement.",
      icon: "ri-bank-card-line",
      gradient: "from-green-500 to-emerald-500"
    }
  ];

  const handleToggle = (index: number) => {
    setOpenIndex(prevIndex => prevIndex === index ? null : index);
  };

  return (
    <section className="relative py-6 sm:py-12 lg:py-16 bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-emerald-200/20 to-teal-300/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-300/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6 sm:mb-10"
        >
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 backdrop-blur-sm rounded-full px-2.5 py-1 sm:px-3 sm:py-1.5 mb-2 sm:mb-3 border border-emerald-200/50">
            <i className="ri-question-answer-line text-emerald-600 text-sm sm:text-base"></i>
            <span className="text-xs font-semibold text-emerald-700">FAQ</span>
          </div>
          <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-2 sm:mb-3">
            Questions Fréquentes
          </h2>
          <p className="text-sm sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Toutes les réponses à vos questions sur la rénovation LED et nos prestations
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-2 sm:space-y-3">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="group"
            >
              <div
                className={`bg-white rounded-xl sm:rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
                  openIndex === index
                    ? 'border-emerald-400 shadow-xl shadow-emerald-100/50'
                    : 'border-slate-200 shadow-md hover:border-emerald-300 hover:shadow-lg'
                }`}
              >
                {/* Question Button */}
                <button
                  type="button"
                  onClick={() => handleToggle(index)}
                  className="w-full text-left px-3 sm:px-5 py-2.5 sm:py-3.5 flex items-center gap-2 sm:gap-3 cursor-pointer transition-colors duration-200"
                >
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br ${faq.gradient} flex items-center justify-center shadow-lg transition-transform duration-300 ${
                    openIndex === index ? 'scale-110 rotate-6' : 'group-hover:scale-105'
                  }`}>
                    <i className={`${faq.icon} text-white text-base sm:text-lg`}></i>
                  </div>

                  {/* Question Text */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs sm:text-lg font-bold text-slate-900 pr-6 sm:pr-8">
                      {faq.question}
                    </h3>
                  </div>

                  {/* Toggle Icon */}
                  <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-100 flex items-center justify-center transition-all duration-300 ${
                    openIndex === index ? 'bg-emerald-100 rotate-180' : 'group-hover:bg-slate-200'
                  }`}>
                    <i className={`ri-arrow-down-s-line text-base sm:text-lg ${
                      openIndex === index ? 'text-emerald-600' : 'text-slate-600'
                    }`}></i>
                  </div>
                </button>

                {/* Answer */}
                <div
                  style={{
                    maxHeight: openIndex === index ? '500px' : '0',
                    opacity: openIndex === index ? 1 : 0,
                  }}
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                >
                  <div className="px-3 sm:px-5 pb-3 sm:pb-5 pt-1 sm:pt-2">
                    <p className="text-xs sm:text-base text-slate-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA at the bottom */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-8 sm:mt-12 text-center"
        >
          <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-2xl">
            <h3 className="text-xl sm:text-3xl font-black text-white mb-2 sm:mb-3">
              Une autre question ?
            </h3>
            <p className="text-emerald-50 mb-4 sm:mb-6 text-xs sm:text-base">
              Nos experts sont là pour vous répondre et vous accompagner dans votre projet
            </p>
            <a
              href="/etude-gratuite"
              className="inline-flex items-center gap-2 bg-white text-emerald-600 font-bold py-2.5 px-6 sm:py-3 sm:px-8 rounded-lg sm:rounded-xl hover:bg-emerald-50 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap text-sm sm:text-base"
            >
              <i className="ri-message-3-line text-base sm:text-lg"></i>
              <span>Demander une étude gratuite</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
